<template>
  <Transition name="fade" mode="out-in">
    <section v-if="currentView === 'list'" key="list" class="question-editor">
      <header v-if="showListHeader" class="editor-heading">
        <h3 class="main-title">{{ $t('form.questions.title') }}</h3>
        <p class="editor-description">{{ $t('form.questions.description') }}</p>
      </header>

      <div v-if="isLoading" class="questions-loading" role="status">
        <span class="spinner-border spinner-border-sm" aria-hidden="true" />
        {{ $t('form.questions.loading') }}
      </div>

      <template v-else>
        <div v-if="!localQuestions.length" class="questions-empty">
          <span class="empty-icon"><IconListDetails :size="24" /></span>
          <h4>{{ $t('form.questions.empty_title') }}</h4>
          <p>{{ $t('form.questions.empty_description') }}</p>
        </div>
        <p v-else class="reorder-hint">
          <IconGripVertical :size="14" />
          {{ $t('form.questions.reorder_hint') }}
        </p>
        <div ref="questionsListEl" class="questions-list">
          <div v-for="(question, index) in localQuestions" :key="question.id" :data-id="question.id">
            <QuestionItem
              :question="question"
              :position="index + 1"
              @edit="openEditView(index)"
              @delete="requestDelete(index)" />
          </div>
        </div>
        <LeadButton class="add-question" :icon-start="IconPlus" @click="openCreateView">
          {{ $t('form.questions.add_new') }}
        </LeadButton>
      </template>
    </section>

    <section v-else-if="editingQuestion" key="edit" class="question-editor">
      <header class="editor-heading">
        <h3 class="main-title">
          {{ $t(isCreatingNew ? 'form.questions.new_question' : 'form.questions.edit_question') }}
        </h3>
        <p class="editor-description">{{ $t('form.questions.edit_description') }}</p>
      </header>
      <fieldset class="question-fields" :disabled="loadingAi || isSaving">
        <QuestionForm v-model="editingQuestion" :errors="apiErrors">
          <template v-if="isCreatingNew" #ai>
            <div class="ai-assistance">
              <LeadButton
                variant="white"
                :icon-start="IconSparkles"
                :loading="loadingAi"
                data-testid="question-generate"
                @click="useAi(editingQuestion!)">
                {{ $t('form.questions.generate_answers') }}
              </LeadButton>
              <LeadAlert v-if="aiError" data-testid="question-generate-error">
                {{ $t('form.questions.ai_generation_failed') }}
              </LeadAlert>
              <p>{{ $t('form.questions.ai_hint') }}</p>
            </div>
          </template>
        </QuestionForm>
      </fieldset>
      <div class="editor-actions">
        <LeadButton variant="white" :disabled="loadingAi || isSaving" @click="closeEditView">
          {{ $t('form.questions.cancel') }}
        </LeadButton>
        <LeadButton :loading="isSaving" :disabled="loadingAi" data-testid="question-save" @click="saveQuestion">
          {{ $t('form.questions.save_question') }}
        </LeadButton>
      </div>
    </section>
  </Transition>

  <ConfirmationDialog
    v-model:dialogShow="showDeleteConfirm"
    :title="$t('form.questions.delete_question')"
    @onContinue="confirmDelete"
    @onCancel="cancelDelete">
    <h1 class="mb-3">{{ $t('form.questions.delete_question') }}</h1>
    <p>
      {{
        $t('form.questions.delete_confirmation_1') +
        (questionToDelete?.title || '') +
        $t('form.questions.delete_confirmation_2')
      }}
    </p>
  </ConfirmationDialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import Sortable from 'sortablejs';
import { IconPlus, IconSparkles, IconListDetails, IconGripVertical } from '@tabler/icons-vue';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';
import { useToast } from 'vue-toastification';
import { ConfirmationDialog } from '@prospective/lithium';
import QuestionItem from './QuestionItem.vue';
import QuestionForm from './QuestionForm.vue';
import {
  listPublicationQuestions,
  createPublicationQuestion,
  updatePublicationQuestion,
  deletePublicationQuestion,
  reorderPublicationQuestions,
  generateAiQuestion,
  getFormTaskStatus,
} from '@/api/questions';
import type { FormQuestionInput, FormQuestionType, IFormQuestion, UiQuestion } from '@/types/formQuestion';
import LeadButton from '../generic/LeadButton.vue';
import translator from '@/locale/translator';
import { useOrderCreationStore } from '@/stores/orderCreation';

interface Props {
  publicationId: string;
  showListHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), { showListHeader: true });

const emit = defineEmits<{
  'questions-updated': [questions: IFormQuestion[]];
  'questions-loading': [loading: boolean];
}>();

const toast = useToast();
const isLoading = ref(true);
watch(isLoading, (loading) => emit('questions-loading', loading), { immediate: true });
// the server questions are the single source of truth
const questions = ref<IFormQuestion[]>([]);
const localQuestions = computed<UiQuestion[]>(() => questions.value.map(fromApiQuestion));
const questionsListEl = ref<HTMLElement | null>(null);

let sortable: Sortable | null = null;
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

const orderCreationStore = useOrderCreationStore();

const currentView = ref<'list' | 'edit'>('list');
const editingQuestion = ref<UiQuestion | null>(null);
const editingIndex = ref<number | null>(null);
const apiErrors = ref<Record<string, string[]> | null>(null);
const isCreatingNew = computed(() => editingIndex.value === null);
const loadingAi = ref(false);
const aiError = ref(false);
const isSaving = ref(false);

// the suggestion error refers to the current wording, so drop it once the question is rephrased
watch(
  () => editingQuestion.value?.title,
  () => (aiError.value = false)
);

const showDeleteConfirm = ref(false);
const questionToDeleteIndex = ref<number | null>(null);
const questionToDelete = computed(() => {
  if (questionToDeleteIndex.value !== null) {
    return localQuestions.value[questionToDeleteIndex.value];
  }
  return null;
});

// --- Data Mapping ---
const toApiQuestion = (q: UiQuestion): Omit<FormQuestionInput, 'id'> => {
  let type: FormQuestionType = 'textarea';
  if (q.type === 'single') type = q.is_dropdown ? 'select' : 'radio';
  else if (q.type === 'multiple') type = 'checkbox';

  const choices =
    q.answers?.map((a) => ({ label: a.label, value: a.label })).filter((c) => c.label.trim() !== '') || [];

  return {
    name: q.title,
    label: q.title,
    description: q.text,
    type: type,
    required: true,
    options: { choices },
  };
};

const fromApiQuestion = (q: IFormQuestion): UiQuestion => {
  let type: UiQuestion['type'] = 'text';
  if (q.type === 'radio' || q.type === 'select') type = 'single';
  else if (q.type === 'checkbox') type = 'multiple';
  return {
    id: q.id,
    title: q.label || '',
    text: q.description || '',
    type: type,
    is_dropdown: q.type === 'select',
    answers: q.options?.choices?.map((c) => ({ id: c.label, label: c.label })) || [],
  };
};

function emitQuestions() {
  emit('questions-updated', [...questions.value]);
}

// --- Lifecycle & View Management ---
onMounted(async () => {
  await checkAndLoadQuestions();
});

watch(questionsListEl, () => {
  sortable?.destroy();
  sortable = null;
  initSortable();
});

onUnmounted(() => {
  if (pollTimeoutId) {
    clearTimeout(pollTimeoutId);
  }
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
});

async function pollQuestionGenerationStatus(publicationId: string): Promise<IFormQuestion[] | null> {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const result = await getFormTaskStatus(publicationId, 'generate_questions');
        // If there is no task, it means generation was not requested.
        if (result.task === null) {
          resolve(null);
        } else if (result.task.status_string === 'success') {
          resolve(result.data);
        } else if (result.task.status_string === 'error') {
          reject(new Error('Question generation failed.'));
        } else {
          pollTimeoutId = setTimeout(poll, 2000); // Poll every 2 seconds
        }
      } catch (error) {
        if ((error as any)?.response?.status === 404) {
          resolve(null);
        } else {
          reject(error);
        }
      }
    };
    poll();
  });
}

async function checkAndLoadQuestions() {
  isLoading.value = true;
  try {
    if (orderCreationStore.generateQuestions) {
      const generatedQuestions = await pollQuestionGenerationStatus(props.publicationId);
      if (generatedQuestions) {
        questions.value = generatedQuestions;
      } else {
        await loadQuestions();
      }
    } else {
      await loadQuestions();
    }
  } catch (error) {
    console.error('Error during question loading process:', error);
    toast.error(translator.get('form.questions.load_error'));
    // Still try to load questions even if polling fails, in case there are some.
    await loadQuestions();
  } finally {
    emitQuestions();
    isLoading.value = false;
  }
}

function openCreateView() {
  editingIndex.value = null;
  editingQuestion.value = {
    id: `new_${Date.now()}`,
    title: '',
    text: '',
    type: 'single',
    is_dropdown: false,
    answers: [
      { id: crypto.randomUUID(), label: '' },
      { id: crypto.randomUUID(), label: '' },
    ],
  };
  apiErrors.value = null;
  currentView.value = 'edit';
}

function openEditView(index: number) {
  editingIndex.value = index;
  editingQuestion.value = JSON.parse(JSON.stringify(localQuestions.value[index]));
  apiErrors.value = null;
  currentView.value = 'edit';
}

function closeEditView() {
  currentView.value = 'list';
  editingQuestion.value = null;
  editingIndex.value = null;
  apiErrors.value = null;
}

// --- API Calls ---
async function loadQuestions() {
  try {
    const response = await listPublicationQuestions(props.publicationId);
    questions.value = response.data;
  } catch (error) {
    console.error('Error loading questions:', error);
    toast.error(translator.get('form.questions.load_error'));
  }
}

function initSortable() {
  if (questionsListEl.value) {
    sortable = Sortable.create(questionsListEl.value, {
      animation: 150,
      handle: '.drag-handle',
      ghostClass: 'ghost',
      onEnd: async (evt) => {
        if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
          const movedItem = questions.value.splice(evt.oldIndex, 1)[0];
          questions.value.splice(evt.newIndex, 0, movedItem);
          await reorderQuestions();
        }
      },
    });
  }
}

async function reorderQuestions() {
  try {
    const questionIds = questions.value.map((q) => q.id).filter((id): id is number => typeof id === 'number');
    if (questionIds.length !== questions.value.length) return;
    await reorderPublicationQuestions(props.publicationId, questionIds);
    emitQuestions();
  } catch (error) {
    console.error('Error reordering questions:', error);
    toast.error(translator.get('form.questions.reorder_failed'));
    await loadQuestions();
  }
}

async function saveQuestion() {
  if (!editingQuestion.value || isSaving.value || loadingAi.value) return;
  apiErrors.value = null;

  if (!editingQuestion.value.title.trim()) {
    apiErrors.value = { name: [translator.get('form.questions.title_required')] };
    return;
  }

  const apiData = toApiQuestion(editingQuestion.value);
  isSaving.value = true;

  try {
    if (isCreatingNew.value) {
      const newApiQuestion = await createPublicationQuestion(props.publicationId, apiData);
      questions.value.push(newApiQuestion.data);
      toast.success(translator.get('form.questions.created'));
    } else if (editingIndex.value !== null) {
      const questionId = editingQuestion.value.id as number;
      const updatedApiQuestion = await updatePublicationQuestion(props.publicationId, questionId, {
        ...apiData,
        id: questionId,
      });
      questions.value.splice(editingIndex.value, 1, updatedApiQuestion.data);
      toast.success(translator.get('form.questions.saved'));
    }
    closeEditView();
    emitQuestions();
  } catch (error: any) {
    if (error?.responseJson?.errors) {
      apiErrors.value = error.responseJson.errors;
    }
    toast.error(translator.get('form.questions.save_failed'));
    console.error('Error saving question:', error);
  } finally {
    isSaving.value = false;
  }
}

function requestDelete(index: number) {
  questionToDeleteIndex.value = index;
  showDeleteConfirm.value = true;
}

function cancelDelete() {
  questionToDeleteIndex.value = null;
  showDeleteConfirm.value = false;
}

async function confirmDelete() {
  if (questionToDeleteIndex.value === null) return;

  const index = questionToDeleteIndex.value;
  const question = questions.value[index];

  questions.value.splice(index, 1);

  if (typeof question.id === 'number') {
    try {
      await deletePublicationQuestion(props.publicationId, question.id);
      toast.success(translator.get('form.questions.deleted'));
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error(translator.get('form.questions.delete_failed'));
      await loadQuestions();
    }
  }

  emitQuestions();

  questionToDeleteIndex.value = null;
  showDeleteConfirm.value = false;
}

type QuestionMutationResult =
  | { success: true; question?: IFormQuestion; questions?: IFormQuestion[] }
  | { success: false; error?: string; errors?: Record<string, string[]> };

async function createQuestion(input: FormQuestionInput): Promise<QuestionMutationResult> {
  try {
    const response = await createPublicationQuestion(props.publicationId, input);
    questions.value.push(response.data);
    emitQuestions();
    closeEditView();
    toast.success(translator.get('form.questions.created'));
    return { success: true, question: response.data };
  } catch (error: any) {
    if (error?.responseJson?.errors) {
      return { success: false, errors: error.responseJson.errors };
    }
    toast.error(translator.get('form.questions.save_failed'));
    return { success: false, error: 'Failed to create question' };
  }
}

async function updateQuestion(id: number, input: FormQuestionInput): Promise<QuestionMutationResult> {
  const index = questions.value.findIndex((q) => q.id === id);
  if (index === -1) return { success: false, error: `Question ${id} not found` };

  try {
    const response = await updatePublicationQuestion(props.publicationId, id, { ...input, id });
    questions.value.splice(index, 1, response.data);
    emitQuestions();
    closeEditView();
    toast.success(translator.get('form.questions.saved'));
    return { success: true, question: response.data };
  } catch (error: any) {
    if (error?.responseJson?.errors) {
      return { success: false, errors: error.responseJson.errors };
    }
    toast.error(translator.get('form.questions.save_failed'));
    return { success: false, error: 'Failed to update question' };
  }
}

async function deleteQuestion(id: number): Promise<QuestionMutationResult> {
  const index = questions.value.findIndex((q) => q.id === id);
  if (index === -1) return { success: false, error: `Question ${id} not found` };

  questions.value.splice(index, 1);
  try {
    await deletePublicationQuestion(props.publicationId, id);
    emitQuestions();
    closeEditView();
    toast.success(translator.get('form.questions.deleted'));
    return { success: true };
  } catch (error) {
    console.error('Error deleting question:', error);
    toast.error(translator.get('form.questions.delete_failed'));
    await loadQuestions();
    emitQuestions();
    return { success: false, error: 'Failed to delete question' };
  }
}

async function reorderQuestionsTo(ids: number[]): Promise<QuestionMutationResult> {
  const byId = new Map(questions.value.map((q) => [q.id, q]));
  const next: IFormQuestion[] = [];
  for (const id of ids) {
    const question = byId.get(id);
    if (!question) return { success: false, error: `Question ${id} not found` };
    next.push(question);
  }
  if (next.length !== questions.value.length) {
    return { success: false, error: 'Reorder must include every question id' };
  }

  const previous = questions.value;
  questions.value = next;
  try {
    await reorderPublicationQuestions(props.publicationId, ids);
    emitQuestions();
    return { success: true, questions: [...questions.value] };
  } catch (error) {
    console.error('Error reordering questions:', error);
    toast.error(translator.get('form.questions.reorder_failed'));
    questions.value = previous;
    await loadQuestions();
    emitQuestions();
    return { success: false, error: 'Failed to reorder questions' };
  }
}

defineExpose({
  createQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions: reorderQuestionsTo,
  isLoading,
});

async function useAi(question: UiQuestion) {
  if (!editingQuestion.value || loadingAi.value || isSaving.value) return;
  apiErrors.value = null;

  if (!question.title || question.title.trim() === '') {
    apiErrors.value = {
      name: [translator.get('form.questions.ai_prompt_required')],
    };
    return;
  }

  aiError.value = false;
  loadingAi.value = true;
  try {
    const response = await generateAiQuestion(props.publicationId, question.title);
    // the backend answers with { data: null } when it can't produce a suggestion for this wording
    if (!response?.data) {
      aiError.value = true;
      return;
    }
    const aiQuestion = fromApiQuestion(response.data);

    editingQuestion.value.text = aiQuestion.text;
    editingQuestion.value.type = aiQuestion.type;
    editingQuestion.value.is_dropdown = aiQuestion.is_dropdown;
    editingQuestion.value.answers = aiQuestion.answers;

    currentView.value = 'edit';
  } catch (error) {
    console.error('Error using AI generation:', error);
    aiError.value = true;
  } finally {
    loadingAi.value = false;
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.main-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.editor-heading {
  margin-bottom: 24px;
}

.editor-description {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.6;
}

.question-editor {
  min-width: 0;
}

.questions-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 0;
  color: #667085;
  font-size: 13px;
}

.questions-list {
  display: grid;
  gap: 12px;
}

.reorder-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 12px;
  color: #667085;
  font-size: 11px;
}

.questions-empty {
  padding: 24px 16px;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  text-align: center;
  background: #fff;
}

.empty-icon {
  display: inline-flex;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 12px;
  color: var(--tblr-primary);
  background: color-mix(in srgb, var(--tblr-primary) 8%, white);
}

.questions-empty h4 {
  margin-bottom: 8px;
  color: #344054;
}

.questions-empty p {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.6;
}

.add-question {
  width: 100%;
  margin-top: 16px;
}

.question-fields {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

.ai-assistance {
  margin-top: -8px;
}

.ai-assistance p {
  margin: 8px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.editor-actions {
  position: sticky;
  bottom: -24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding: 16px 0;
  border-top: 1px solid #e4e7ec;
  background: #f9fafb;
  z-index: 1;
}

.ghost {
  opacity: 0.4;
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
